// Access helpers shared by the sidebar and the admin layout so route visibility and the
// post-login landing redirect agree on who a user is.
//
// Roles model:
//  - userRole (int): 1 = admin console user, 2 = laundry user.
//  - userRoles (string): pipe-separated permission tokens (e.g. "ADMIN|USERS|ORDERS").
//  - posBranchId: the laundry assigned as the user's POS branch. Any non-null value grants
//    POS access; the login also fills laundryName/phone/address from that branch for receipts.

// Reads a localStorage value, normalising the literal "null"/"undefined" strings (which
// setItem produces from null/undefined) and empty strings to null.
const readStored = (key) => {
    const raw = localStorage.getItem(key);
    if (!raw || raw === "null" || raw === "undefined") {
        return null;
    }
    return raw;
};

export const getRoleTokens = () => {
    const raw = readStored("userRoles");
    if (!raw) {
        return [];
    }
    return raw
        .split("|")
        .map((t) => t.trim().toUpperCase())
        .filter(Boolean);
};

export const isAdminUser = () => getRoleTokens().includes("ADMIN");

export const isLaundryUser = () => localStorage.getItem("userRole") == 2;

// A user has a POS branch assigned (pos_branch_id not null) → they get POS access.
export const hasPosBranch = () => !!readStored("posBranchId");

// True when the user can reach the POS screens at all: full admins, or anyone with a POS
// branch assigned.
export const canAccessPos = () => isAdminUser() || hasPosBranch();

// The route `role` bucket this user falls in. Mirrors the sidebar's own mapping.
export const getRoleName = () => {
    const userRole = localStorage.getItem("userRole");
    if (userRole == 1) {
        return "admin";
    }
    if (userRole == 2) {
        return "laundry";
    }
    return "demo";
};

// True when the user may reach a single (non-collapse) route entry.
export const canAccessRoute = (route) => {
    if (route.adminOnly && !isAdminUser()) {
        return false;
    }
    if (route.requiresPosAccess && !canAccessPos()) {
        return false;
    }
    if (route.role !== getRoleName()) {
        return false;
    }
    if (route.permission && !getRoleTokens().includes(route.permission)) {
        return false;
    }
    return true;
};

// The path to land a user on, walking the routes top to bottom and taking the first one they
// can actually reach. Admins get the Dashboard (it is first); everyone else falls through to
// POS, then Orders, then whatever their tokens allow. Returns null when the user can reach
// nothing at all — callers must not redirect in that case, or they spin.
export const getLandingPath = (routes) => {
    for (const route of routes || []) {
        if (route.redirect || route.invisible || route.divider || route.title) {
            continue;
        }
        if (route.collapse) {
            if (route.requiresPosAccess && !canAccessPos()) {
                continue;
            }
            const nested = getLandingPath(route.views);
            if (nested) {
                return nested;
            }
            continue;
        }
        if (route.layout !== "/admin" || !route.path) {
            continue;
        }
        if (canAccessRoute(route)) {
            return route.layout + route.path;
        }
    }
    return null;
};
